import React, {useState, useEffect} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthService, ResourceService } from "../../services";
import { Archive, ArrowClockwise } from "react-bootstrap-icons";
import { Spinner, Breadcrumb, BreadcrumbItem, Tabs, Tab, Modal, Button } from "react-bootstrap";
import { RESOURCE_TYPE_OPTIONS, RESOURCE_SPECIALTY_OPTIONS, getProviderPhoneFieldCount } from "../../shared/constants";
import { useUnsavedChangesGuard, useFormDirtyTracker } from "../../shared";

const MAX_PROVIDER_PHONES = 5;

const UpdateResource = () => {
  const navigate = useNavigate();
  const urlParams = useParams();
  const [currentResource, setCurrentResource] = useState(undefined);
  
  // Basic Information
  const [name, setName] = useState(''); // Provider name
  const [officeName, setOfficeName] = useState('');
  const [title, setTitle] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [specialtyOther, setSpecialtyOther] = useState('');
  const [type, setType] = useState('');
  const [typeOther, setTypeOther] = useState('');
  
  // Contact Information
  const [phone, setPhone] = useState(''); // Primary Phone Number
  const [contact, setContact] = useState(''); // Phone Number 2
  const [phone3, setPhone3] = useState('');
  const [phone4, setPhone4] = useState('');
  const [phone5, setPhone5] = useState('');
  const [phoneFieldCount, setPhoneFieldCount] = useState(1);
  const [fax, setFax] = useState(''); // Fax Number
  const [email, setEmail] = useState('');
  
  // Address (split fields)
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');
  
  // Additional Information
  const [note, setNote] = useState('');
  
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const isArchivedProvider = `${currentResource?.status || ''}`.toLowerCase() === 'inactive';

  const { isDirty, resetBaseline } = useFormDirtyTracker({
    name, officeName, title, specialty, specialtyOther, type, typeOther,
    phone, contact, phone3, phone4, phone5, fax, email,
    addressLine1, addressLine2, city, state, zipcode, note,
  });
  const { tryNavigate, tryGoBack } = useUnsavedChangesGuard(isDirty);

  const goBack = () => {
    tryGoBack(`/medical/resources/list`);
  };

  const redirectToList = () => {
    tryNavigate(`/medical/resources/list`);
  };

  const redirectToView = () => {
    resetBaseline();
    tryNavigate(`/medical/resources/${urlParams.id}`);
  };

  const validateResource = () => {
    const errors = [];
    
    // Required fields validation
    if (!name || name.trim() === '') {
      errors.push('Provider');
    }
    if (!phone || phone.trim() === '') {
      errors.push('Primary Phone Number');
    }
    if (!fax || fax.trim() === '') {
      errors.push('Fax Number');
    }
    if (type === 'other' && (!typeOther || typeOther.trim() === '')) {
      errors.push('Other-please specify');
    }
    if (specialty === 'other' && (!specialtyOther || specialtyOther.trim() === '')) {
      errors.push('Other Specialty-please specify');
    }

    if (errors.length > 0) {
      window.alert(`Please fill in the following required fields:\n${errors.join('\n')}`);
      return false;
    }
    return true;
  };

  const saveResource = () => {
    if (!validateResource()) {
      return;
    }
    
    const combinedAddress = `${addressLine1}${addressLine2 ? ', ' + addressLine2 : ''}, ${city}, ${state} ${zipcode}`.trim();
    
    const updatedResource = {
      ...currentResource,
      data: currentResource?.data || {},
      
      // Basic Information
      name, // Provider
      office_name: officeName,
      name_original: officeName, // Legacy field
      title,
      specialty,
      specialty_other: specialty === 'other' ? specialtyOther : '',
      type,
      type_other: type === 'other' ? typeOther : '',
      
      // Contact Information
      phone, // Primary Phone Number
      contact: phoneFieldCount >= 2 ? contact : '',
      phone3: phoneFieldCount >= 3 ? phone3 : '',
      phone4: phoneFieldCount >= 4 ? phone4 : '',
      phone5: phoneFieldCount >= 5 ? phone5 : '',
      fax, // Fax Number
      email,
      
      // Address (split fields)
      address_line_1: addressLine1,
      address_line_2: addressLine2,
      city,
      state,
      zipcode,
      address: combinedAddress, // Legacy field
      
      // Additional Information
      note,
      
      // System fields
      edit_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
      edit_date: new Date(),
      edit_history: currentResource?.edit_history 
        ? [...currentResource.edit_history, { employee: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name, date: new Date() }] 
        : [{ employee: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name, date: new Date() }]
    };
    
    ResourceService.updateResource(urlParams.id, updatedResource).then(() => redirectToView());
  };

  const updateResourceStatus = (nextStatus) => {
    if (!AuthService.canEditProviderInfo()) return;
    ResourceService.disableResource(urlParams.id, {
      status: nextStatus,
      edit_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name,
      edit_date: new Date()
    }).then(() => {
      setShowArchiveConfirm(false);
      resetBaseline();
      redirectToList();
    });
  }

  useEffect(() => {
    if (!AuthService.canEditProviderInfo()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    ResourceService.getResource(urlParams.id).then(resourceData => {
      setCurrentResource(resourceData.data);
    });
  }, []);

  useEffect(() => {
    if (currentResource) {
      // Basic Information
      setName(currentResource?.name || '');
      setOfficeName(currentResource?.office_name || currentResource?.name_original || '');
      setTitle(currentResource?.title || '');
      setSpecialty(currentResource?.specialty || '');
      setSpecialtyOther(currentResource?.specialty_other || '');
      setType(currentResource?.type || '');
      setTypeOther(currentResource?.type_other || '');
      
      // Contact Information
      setPhone(currentResource?.phone || '');
      setContact(currentResource?.contact || '');
      setPhone3(currentResource?.phone3 || '');
      setPhone4(currentResource?.phone4 || '');
      setPhone5(currentResource?.phone5 || '');
      setPhoneFieldCount(getProviderPhoneFieldCount(currentResource));
      setFax(currentResource?.fax || '');
      setEmail(currentResource?.email || '');
      
      // Address - try new fields first, then fall back to legacy
      setAddressLine1(currentResource?.address_line_1 || currentResource?.address || '');
      setAddressLine2(currentResource?.address_line_2 || '');
      setCity(currentResource?.city || '');
      setState(currentResource?.state || '');
      setZipcode(currentResource?.zipcode || '');
      
      // Additional Information - merge description into note if note is empty
      setNote(currentResource?.note || currentResource?.description || '');
      const t = window.setTimeout(() => resetBaseline(), 0);
      return () => window.clearTimeout(t);
    }
  }, [currentResource]);

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/dashboard/dashboard">Medical</Breadcrumb.Item>
          <Breadcrumb.Item href="/medical/resources/list">
            Provider Information
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            Update Provider Information
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4>Update Provider Information <button className="btn btn-link btn-sm" onClick={() => {goBack()}}>Back</button></h4> 
        </div>
      </div>
      <div className="app-main-content-list-container">
        <div className="app-main-content-list-func-container">
          <Tabs defaultActiveKey="providerInfo" id="providers-tab">
            <Tab eventKey="providerInfo" title="Provider Information">
              <div className="pt-3">
                <h6 className="text-primary mb-3">Basic Information</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Provider <span className="required">*</span></div>
                  <input placeholder="e.g., Dr. Cao Qing" type="text" value={name} onChange={e => setName(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Office Name</div>
                  <input type="text" placeholder="e.g., Silver Spring Family Medicine Clinic" value={officeName} onChange={e => setOfficeName(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Title</div>
                  <input type="text" placeholder="e.g., Medical Director" value={title} onChange={e => setTitle(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Type</div>
                  <select value={type} onChange={e => {
                    const nextType = e.target.value;
                    setType(nextType);
                    if (nextType !== 'other') {
                      setTypeOther('');
                    }
                  }}>
                    <option value="">Select...</option>
                    {RESOURCE_TYPE_OPTIONS.map((item, index) => (
                      <option key={index} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </div>
                {type === 'other' && <div className="me-4">
                  <div className="field-label">Other-please specify</div>
                  <input type="text" value={typeOther} onChange={e => setTypeOther(e.target.value)} />
                </div>}
                <div className="me-4">
                  <div className="field-label">Specialty</div>
                  <select
                    value={specialty}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSpecialty(next);
                      if (next !== 'other') {
                        setSpecialtyOther('');
                      }
                    }}
                  >
                    <option value="">Select...</option>
                    {RESOURCE_SPECIALTY_OPTIONS.map((item, index) => (
                      <option key={index} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </div>
                {specialty === 'other' && (
                  <div className="me-4">
                    <div className="field-label">Other Specialty-please specify <span className="required">*</span></div>
                    <input
                      type="text"
                      placeholder="e.g., Prosthetics & Orthotics"
                      value={specialtyOther}
                      onChange={(e) => setSpecialtyOther(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <h6 className="text-primary">Contact Information</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Primary Phone Number <span className="required">*</span></div>
                  <input placeholder="e.g., 240-463-1098" type="text" value={phone} onChange={e => setPhone(e.target.value)}/>
                </div>
                {phoneFieldCount >= 2 && (
                  <div className="me-4">
                    <div className="field-label">Phone Number 2</div>
                    <input type="text" placeholder="e.g., 240-463-1698" value={contact} onChange={e => setContact(e.target.value)}/>
                  </div>
                )}
                {phoneFieldCount >= 3 && (
                  <div className="me-4">
                    <div className="field-label">Phone Number 3</div>
                    <input type="text" placeholder="e.g., 240-463-1698" value={phone3} onChange={e => setPhone3(e.target.value)}/>
                  </div>
                )}
                {phoneFieldCount >= 4 && (
                  <div className="me-4">
                    <div className="field-label">Phone Number 4</div>
                    <input type="text" placeholder="e.g., 240-463-1698" value={phone4} onChange={e => setPhone4(e.target.value)}/>
                  </div>
                )}
                {phoneFieldCount >= 5 && (
                  <div className="me-4">
                    <div className="field-label">Phone Number 5</div>
                    <input type="text" placeholder="e.g., 240-463-1698" value={phone5} onChange={e => setPhone5(e.target.value)}/>
                  </div>
                )}
                {phoneFieldCount < MAX_PROVIDER_PHONES && (
                  <div className="me-4 d-flex align-items-end pb-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setPhoneFieldCount((count) => Math.min(MAX_PROVIDER_PHONES, count + 1))}
                    >
                      + Add
                    </button>
                  </div>
                )}
                <div className="me-4">
                  <div className="field-label">Fax Number <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., 240-463-1698" value={fax} onChange={e => setFax(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Email</div>
                  <input type="email" placeholder="e.g., example@gmail.com" value={email} onChange={e => setEmail(e.target.value)}/>
                </div>
              </div>

              <h6 className="text-primary">Provider Address</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Address Line 1 <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., 555 Cloverly Forest Dr" value={addressLine1} onChange={e => setAddressLine1(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Address Line 2</div>
                  <input type="text" placeholder="e.g., Suite 200" value={addressLine2} onChange={e => setAddressLine2(e.target.value)}/>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">City <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., Rockville" value={city} onChange={e => setCity(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">State <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., MD" value={state} onChange={e => setState(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Zip Code <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., 20850" value={zipcode} onChange={e => setZipcode(e.target.value)}/>
                </div>
              </div>

              <h6 className="text-primary">Additional Information</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Note</div>
                  <textarea 
                    placeholder="e.g., Preferred provider for cardiology referrals" 
                    value={note} 
                    onChange={e => setNote(e.target.value)}
                    rows={4}
                    style={{width: '400px'}}
                  />
                </div>
              </div>

              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveResource()}> Save </button>
                </div>
              </div>
              </div>
            </Tab>
          </Tabs>
          <div className="list-func-panel">
            {AuthService.canEditProviderInfo() && (
              <button type="button" className="btn btn-primary" onClick={() => setShowArchiveConfirm(true)}>
                {isArchivedProvider ? (
                  <ArrowClockwise size={16} className="me-2" aria-hidden />
                ) : (
                  <Archive size={16} className="me-2" aria-hidden />
                )}
                {isArchivedProvider ? 'Reactivate' : 'Archive'}
              </button>
            )}
          </div>
        </div>
      </div>
      <Modal show={showArchiveConfirm} onHide={() => setShowArchiveConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{isArchivedProvider ? 'Reactivate provider' : 'Archive provider'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isArchivedProvider
            ? 'Do you really want to reactivate this provider?'
            : 'Do you really want to archive this provider?'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowArchiveConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => updateResourceStatus(isArchivedProvider ? 'active' : 'inactive')}>
            {isArchivedProvider ? 'Reactivate' : 'Archive'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UpdateResource;
